import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaMicrophone,
  FaStar,
  FaBullseye,
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  submitConsultationForm,
  validateFormData,
} from "../services/formService";
import CountrySelector from "./CountrySelector";
import "./ConsultationSection.css";

const ConsultationSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    countryCode: "+91",
    country: { code: "IN", name: "India", dialCode: "+91" },
    goals: [],
    trainingStartDate: "",
    budget: "",
    specificExpert: "",
    customExpert: "",
  });

  const [formState, setFormState] = useState({
    isSubmitting: false,
    errors: {},
    message: "",
  });

  const singingGoals = [
    "Become a Playback Singer",
    "Master Carnatic Singing",
    "Want to Crack Reality Shows",
    "How to improve Voice? (Voice Culture, exercise)",
    "Western / Hindustani",
    "Want to Learn Film Sing",
    "Others",
  ];

  const trainingStartOptions = [
    "Immediately",
    "7-14 days",
    "After 30 days",
    "After 2-3 months",
    "Not sure yet",
  ];

  const budgetOptions = [
    "Lower than 3000",
    "3000-4000",
    "4000-5000",
    "5000-7000",
    "7000+",
    "Flexible budgets (Quality Learning is important)",
  ];

  const expertOptions = [
    "Bhargavi Sridhar",
    "Ramya NSK",
    "Monisha Rathinavelu",
    "Charumathi Muralidaran",
    "Abishek Raju",
    "Reshmi",
    "Aparna Narayanan",
    "Abburva Dilip",
    "Other",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for phone input
    if (name === "phone") {
      // For India, only allow digits and limit to 10 characters
      if (formData.countryCode === "+91") {
        const digitsOnly = value.replace(/\D/g, ""); // Remove non-digits
        const limitedDigits = digitsOnly.slice(0, 10); // Limit to 10 digits
        setFormData((prev) => ({
          ...prev,
          [name]: limitedDigits,
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGoalChange = (goal) => {
    setFormData((prev) => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter((g) => g !== goal)
        : [...prev.goals, goal],
    }));
  };

  const handleCountryChange = (country) => {
    setFormData((prev) => ({
      ...prev,
      country: country,
      countryCode: country.dialCode,
      phone: "", // Clear phone number when country changes
    }));
  };

  // Get phone placeholder based on selected country
  const getPhonePlaceholder = (countryCode) => {
    const placeholders = {
      "+1": "(555) 123-4567", // US/Canada
      "+91": "9876543210", // India (10 digits only)
      "+44": "7700 900123", // UK
      "+49": "151 12345678", // Germany
      "+33": "6 12 34 56 78", // France
      "+86": "138 0013 8000", // China
      "+81": "90-1234-5678", // Japan
      "+55": "(11) 99999-9999", // Brazil
      "+61": "412 345 678", // Australia
      "+7": "912 345-67-89", // Russia
      "+39": "320 123 4567", // Italy
      "+34": "612 34 56 78", // Spain
      "+82": "10-1234-5678", // South Korea
      "+65": "9123 4567", // Singapore
      "+971": "50 123 4567", // UAE
      "+966": "50 123 4567", // Saudi Arabia
      "+27": "82 123 4567", // South Africa
      "+234": "802 123 4567", // Nigeria
      "+20": "10 1234 5678", // Egypt
      "+52": "55 1234 5678", // Mexico
      "+54": "11 1234-5678", // Argentina
      "+90": "532 123 45 67", // Turkey
      "+31": "6 12345678", // Netherlands
      "+46": "70-123 45 67", // Sweden
      "+47": "412 34 567", // Norway
      "+45": "20 12 34 56", // Denmark
      "+358": "40 123 4567", // Finland
      "+41": "79 123 45 67", // Switzerland
      "+43": "664 123456", // Austria
      "+32": "470 12 34 56", // Belgium
      "+48": "512 345 678", // Poland
      "+420": "601 123 456", // Czech Republic
      "+36": "20 123 4567", // Hungary
      "+40": "721 234 567", // Romania
      "+359": "87 123 4567", // Bulgaria
      "+30": "694 123 4567", // Greece
      "+351": "91 123 4567", // Portugal
      "+353": "85 123 4567", // Ireland
      "+64": "21 123 4567", // New Zealand
      "+66": "81 234 5678", // Thailand
      "+60": "12-345 6789", // Malaysia
      "+62": "812-3456-7890", // Indonesia
      "+63": "917 123 4567", // Philippines
      "+84": "90 123 45 67", // Vietnam
      "+880": "1712 345678", // Bangladesh
      "+92": "300 1234567", // Pakistan
      "+94": "77 123 4567", // Sri Lanka
      "+977": "9841 234567", // Nepal
      "+975": "17 12 34 56", // Bhutan
      "+960": "771 2345", // Maldives
      "+93": "70 123 4567", // Afghanistan
      "+98": "912 345 6789", // Iran
      "+964": "750 123 4567", // Iraq
      "+972": "50-123-4567", // Israel
      "+962": "77 123 4567", // Jordan
      "+961": "71 123 456", // Lebanon
      "+965": "9999 1234", // Kuwait
      "+974": "3312 3456", // Qatar
      "+973": "3312 3456", // Bahrain
      "+968": "9123 4567", // Oman
      "+967": "712 345 678", // Yemen
      "+963": "944 123 456", // Syria
      "+357": "99 123 456", // Cyprus
      "+356": "9999 1234", // Malta
      "+354": "820 1234", // Iceland
      "+352": "621 123 456", // Luxembourg
      "+423": "79 123 45 67", // Liechtenstein
      "+377": "6 12 34 56 78", // Monaco
      "+378": "66 123 456", // San Marino
      "+379": "66 123 456", // Vatican City
      "+376": "312 345", // Andorra
      "+375": "29 123-45-67", // Belarus
      "+380": "50 123 45 67", // Ukraine
      "+373": "69 123 456", // Moldova
      "+370": "612 34567", // Lithuania
      "+371": "2123 4567", // Latvia
      "+372": "5123 4567", // Estonia
      "+386": "40 123 456", // Slovenia
      "+421": "901 234 567", // Slovakia
      "+385": "91 234 5678", // Croatia
      "+387": "61 123 456", // Bosnia and Herzegovina
      "+381": "60 123 4567", // Serbia
      "+382": "67 123 456", // Montenegro
      "+389": "70 123 456", // North Macedonia
      "+355": "69 123 4567", // Albania
      "+383": "44 123 456", // Kosovo
    };

    return placeholders[countryCode] || "(555) 123-4567";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset previous errors
    setFormState((prev) => ({
      ...prev,
      errors: {},
      message: "",
    }));

    // Validate form data
    const validation = validateFormData(formData);
    if (!validation.isValid) {
      setFormState((prev) => ({
        ...prev,
        errors: validation.errors,
      }));
      return;
    }

    // Set submitting state
    setFormState((prev) => ({
      ...prev,
      isSubmitting: true,
    }));

    try {
      // Prepare form data for submission
      const submissionData = {
        ...formData,
        // If "Other" is selected, use customExpert value, otherwise use specificExpert
        specificExpert: formData.specificExpert === "Other" 
          ? formData.customExpert 
          : formData.specificExpert
      };

      // Submit to Firebase
      const result = await submitConsultationForm(submissionData);

      if (result.success) {
        // Navigate to success page with form data
        navigate("/success", {
          state: {
            formData: submissionData,
            message: result.message,
          },
        });
      } else {
        setFormState({
          isSubmitting: false,
          isSubmitted: false,
          errors: { submit: result.message },
          message: "",
        });
      }
    } catch (error) {
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        errors: { submit: "An unexpected error occurred. Please try again." },
        message: "",
      });
    }
  };

  const benefits = [
    {
      icon: <FaMicrophone className="benefit-icon" />,
      title: "Personalized Matching",
      description:
        "We'll match you with a coach who specializes in your preferred genre and skill level.",
    },
    {
      icon: <FaStar className="benefit-icon" />,
      title: "Industry Professionals",
      description:
        "Learn from coaches who have worked in the industry for more than 10 years.",
    },
    {
      icon: <FaBullseye className="benefit-icon" />,
      title: "Results-Driven",
      description:
        "98% of our students see significant improvement within their first month.",
    },
  ];

  return (
    <section id="consultation" className="consultation-section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="section-header"
        >
          <h2 className="highlight">Get your Free Consultation</h2>
          <p>
            Get matched with an expert who suits your style and goals
            
          </p>
        </motion.div>

        <div className="consultation-content">
          {/* Form Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="form-section"
          >
            <div className="form-header">
              <h3>
                Ready to <span className="highlight">Transform</span> Your
                Voice?
              </h3>
              {/* <p>
                Tell us about your singing goals and we'll match you with the
                perfect expert coach.
              </p> */}
            </div>

            <form onSubmit={handleSubmit} className="consultation-form">
              {/* Error Message */}
              {formState.errors.submit && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="error-message"
                >
                  <FaExclamationCircle className="error-icon" />
                  <p>{formState.errors.submit}</p>
                </motion.div>
              )}

              {/* 1. Name */}
              <div className="form-group">
                <label htmlFor="fullName" className="form-label">
                  Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={`form-input ${
                    formState.errors.fullName ? "error" : ""
                  }`}
                  placeholder="Enter your full name"
                  required
                />
                {formState.errors.fullName && (
                  <span className="field-error">
                    {formState.errors.fullName}
                  </span>
                )}
              </div>

              {/* 2. WhatsApp Contact Number */}
              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  WhatsApp Contact Number *
                </label>
                <div className="phone-input-container">
                  <CountrySelector
                    selectedCountry={formData.country}
                    onCountryChange={handleCountryChange}
                    className="country-selector-wrapper"
                  />
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`form-input phone-input ${
                      formState.errors.phone ? "error" : ""
                    }`}
                    placeholder={getPhonePlaceholder(formData.countryCode)}
                    required
                  />
                </div>
                {formState.errors.phone && (
                  <span className="field-error">{formState.errors.phone}</span>
                )}
              </div>

              {/* 3. Training Start Date */}
              <div className="form-group">
                <label htmlFor="trainingStartDate" className="form-label">
                  From when are you planning to start your training? *
                </label>
                <select
                  id="trainingStartDate"
                  name="trainingStartDate"
                  value={formData.trainingStartDate}
                  onChange={handleInputChange}
                  className={`form-input form-select ${
                    formState.errors.trainingStartDate ? "error" : ""
                  }`}
                  required
                >
                  <option value="">Choose</option>
                  {trainingStartOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {formState.errors.trainingStartDate && (
                  <span className="field-error">
                    {formState.errors.trainingStartDate}
                  </span>
                )}
              </div>

              {/* 4. Specific Expert Preference */}
              <div className="form-group">
                <label htmlFor="specificExpert" className="form-label">
                  Any specific expert you want 1:1 training from?
                </label>
                <select
                  id="specificExpert"
                  name="specificExpert"
                  value={formData.specificExpert}
                  onChange={handleInputChange}
                  className="form-input form-select"
                >
                  <option value="">Choose an expert</option>
                  {expertOptions.map((expert, index) => (
                    <option key={index} value={expert}>
                      {expert}
                    </option>
                  ))}
                </select>
                
                {/* Show custom expert input when "Other" is selected */}
                {formData.specificExpert === "Other" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="custom-expert-input"
                  >
                    <input
                      type="text"
                      id="customExpert"
                      name="customExpert"
                      value={formData.customExpert}
                      onChange={handleInputChange}
                      className={`form-input ${
                        formState.errors.customExpert ? "error" : ""
                      }`}
                      placeholder="Enter expert name"
                      required
                    />
                    {formState.errors.customExpert && (
                      <span className="field-error">
                        {formState.errors.customExpert}
                      </span>
                    )}
                  </motion.div>
                )}
              </div>

              {/* 5. Learning Goals */}
              <div className="form-group">
                <label className="form-label">
                  Share your learning goals from these experts (choose multiple
                  options if so) *
                </label>
                <div
                  className={`goals-checkbox-container ${
                    formState.errors.goals ? "error" : ""
                  }`}
                >
                  {singingGoals.map((goal, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="goal-checkbox-item"
                    >
                      <input
                        type="checkbox"
                        id={`goal-${index}`}
                        checked={formData.goals.includes(goal)}
                        onChange={() => handleGoalChange(goal)}
                        className="goal-checkbox"
                      />
                      <label htmlFor={`goal-${index}`} className="goal-label">
                        {goal}
                      </label>
                    </motion.div>
                  ))}
                </div>
                {formState.errors.goals && (
                  <span className="field-error">{formState.errors.goals}</span>
                )}
              </div>

              {/* 6. Budget */}
              <div className="form-group">
                <label htmlFor="budget" className="form-label">
                  How much are you willing to pay per month for 1:1 expert
                  coaching? *
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className={`form-input form-select ${
                    formState.errors.budget ? "error" : ""
                  }`}
                  required
                >
                  <option value="">Choose</option>
                  {budgetOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {formState.errors.budget && (
                  <span className="field-error">{formState.errors.budget}</span>
                )}
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: formState.isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: formState.isSubmitting ? 1 : 0.98 }}
                className="btn btn-primary submit-btn"
                disabled={formState.isSubmitting}
              >
                {formState.isSubmitting ? (
                  <>
                    <FaSpinner className="spinner" />
                    Submitting...
                  </>
                ) : (
                  "Get my Free Consultation"
                )}
              </motion.button>

              <p className="privacy-text">
                We respect your privacy. Your information is secure and will
                never be shared.
              </p>
            </form>
          </motion.div>

          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="benefits-section"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.2 }}
                viewport={{ once: true }}
                className="benefit-card"
              >
                <div className="benefit-icon-wrapper">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default ConsultationSection;
